import React from "react";
import { DateField, List, useDataGrid } from "@refinedev/mui";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  BaseRecord,
  CrudFilters,
  getDefaultFilter,
  HttpError,
  IResourceComponentsProps,
  useTranslate,
} from "@refinedev/core";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "@refinedev/react-hook-form";
import { AddressCell } from "../../components/table/AdressCell";
import { getDuration } from "../../utils/getDuration";
import { Controller } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

export interface IAlertHistory {
  id: number;
  user: string;
  genre: string;
  hasBeenOnboarded: boolean;
}

export interface IAlertHistoryFilterVariables {
  q?: string;
  createdAt?: Dayjs | null;
  status?: string;
}

export const AlertHistoryList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { dataGridProps, search, filters } = useDataGrid<
    IAlertHistory,
    HttpError,
    IAlertHistoryFilterVariables
  >({
    initialPageSize: 10,
    meta: {
      join: [
        { field: "user" },
        { field: "user.subGroup" }
      ]
    },
    onSearch: (params) => {
      const filters: CrudFilters = [];
      const { q, createdAt, status } = params;

      // Recherche globale dans nom d'utilisateur, adresse
      if (q && q !== "") {
        filters.push({
          field: "q",
          operator: "eq",
          value: q,
        });
      }

      // Filtre par date
      if (createdAt) {
        const startOfDay = createdAt.startOf('day').toISOString();
        const endOfDay = createdAt.endOf('day').toISOString();

        filters.push({
          field: "createdAt",
          operator: "gte",
          value: startOfDay,
        });

        filters.push({
          field: "createdAt",
          operator: "lte",
          value: endOfDay,
        });
      }

      // Filtre par statut (en cours / terminée)
      if (status && status !== "") {
        filters.push({
          field: "isBroadcasting",
          operator: "eq",
          value: status === "en_cours" ? true : false,
        });
      }

      return filters;
    },
    pagination: { mode: "server" },
  });

  const { register, handleSubmit, control } = useForm<
    BaseRecord,
    HttpError,
    IAlertHistoryFilterVariables
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq"),
      createdAt: null,
      status: undefined,
    },
  });

  const userLocale = navigator.language;

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: "user",
        flex: 1,
        headerName: translate("alert-history.fields.user"),
        minWidth: 250,
        renderCell: function render({ value }) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <span>{value.fullname || value.email}</span>
              {value.subGroup?.name && (
                <Chip
                  icon={<AccountTreeIcon sx={{ fontSize: '14px !important' }} />}
                  label={value.subGroup.name}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: '22px',
                    '& .MuiChip-label': {
                      px: 0.75,
                      fontSize: '0.75rem',
                    },
                    '& .MuiChip-icon': {
                      color: 'primary.main',
                      ml: 0.5,
                    },
                  }}
                />
              )}
            </Box>
          );
        },
      },
      {
        field: "createdAt",
        flex: 1,
        headerName: translate("alert-history.fields.createdAt"),
        minWidth: 150,
        renderCell: function render({ value }) {
          return <DateField locales={userLocale} value={value} />;
        },
      },
      {
        field: "Adresse",
        flex: 1,
        headerName: translate("alert-history.fields.address"),
        type: "string",
        minWidth: 300,
        renderCell: function render(value) {
          return (
            <AddressCell
              latitude={value.row.latitude}
              longitude={value.row.longitude}
            />
          );
        },
      },
      {
        field: "status",
        flex: 0.5,
        headerName: translate("alert-history.fields.status"),
        minWidth: 120,
        renderCell: function render({ row }) {
          const isBroadcasting = row.isBroadcasting;
          return (
            <Chip
              label={isBroadcasting ? translate("alert-history.status.ongoing") : translate("alert-history.status.ended")}
              color={isBroadcasting ? "success" : "default"}
              size="small"
            />
          );
        },
      },
      {
        field: "duration",
        flex: 0.5,
        headerName: translate("alert-history.fields.duration"),
        minWidth: 80,
        renderCell: function render(value) {
          const calculateDuration = getDuration({
            createdAt: value?.row.createdAt,
            endedAt: value?.row.updatedAt,
          });
          return <p>{calculateDuration}</p>;
        },
      },
      {
        field: "actions",
        headerName: translate("table.actions"),
        sortable: false,
        renderCell: function render({ row }) {
          const handleClick = () => {
            // Redirect to monitoring for live alerts, alert-history for ended ones
            if (row.isBroadcasting) {
              navigate(`/monitoring/${row.id}`);
            } else {
              navigate(`/alert-history/show/${row.id}`);
            }
          };

          return (
            <Tooltip title="Voir">
              <IconButton
                component={Link}
                to={`/alert-history/${row.id}`}
                size="small"
                color="primary"
              >
                <Visibility />
              </IconButton>
            </Tooltip>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 80,
      },
    ],
    [translate, userLocale, navigate]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={3}>
          <Card>
            <CardHeader title={translate("filters.title")} />
            <CardContent sx={{ pt: 0 }}>
              <Box
                component="form"
                sx={{ display: "flex", flexDirection: "column" }}
                autoComplete="off"
                onSubmit={handleSubmit(search)}
              >
                <TextField
                  {...register("q")}
                  label={translate("alert-history.filters.search")}
                  placeholder={translate("alert-history.filters.searchPlaceholder")}
                  margin="normal"
                  fullWidth
                  autoFocus
                  size="small"
                />

                <Controller
                  control={control}
                  name="createdAt"
                  render={({ field }) => (
                    <DatePicker
                      label="Date de l'alerte"
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                      }}
                      slotProps={{
                        textField: {
                          margin: "normal",
                          size: "small",
                          fullWidth: true,
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Autocomplete
                      options={["en_cours", "terminee"]}
                      value={field.value ?? null}
                      onChange={(_, value) => {
                        field.onChange(value);
                      }}
                      isOptionEqualToValue={(option, value) => option === value}
                      getOptionLabel={(item) => {
                        return item === "en_cours" ? translate("alert-history.status.ongoing") : translate("alert-history.status.ended");
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={translate("alert-history.filters.status")}
                          placeholder={translate("alert-history.filters.statusPlaceholder")}
                          margin="normal"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    />
                  )}
                />
                <br />
                <Button type="submit" variant="contained">
                  {translate("buttons.filter")}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      <Grid item xs={12} lg={9}>
        <List>
          <DataGrid
            {...dataGridProps}
            columns={columns}
            autoHeight
            filterModel={undefined}
            pageSizeOptions={[10, 25, 50, 100]}
            getRowClassName={(params) =>
              params.row.isBroadcasting ? "row-broadcasting" : ""
            }
            sx={{
              "& .row-broadcasting": {
                backgroundColor: "#e8f5e9", // Light green (MUI green[50])
                "&:hover": {
                  backgroundColor: "#c8e6c9", // Slightly darker on hover (MUI green[100])
                },
              },
            }}
          />
        </List>
      </Grid>
      </Grid>
    </LocalizationProvider>
  );
};
